import { PhoneCallIcon, SparklesIcon, MicIcon, FileTextIcon } from 'lucide-react'
import { useAuth } from '@/lib/firebase/auth'
import { Button } from '@/components/ui/button'
import { SoundWave } from '@/components/shared/SoundWave'
import { ThemeToggle } from '@/components/layout/ThemeToggle'
import { EmailAuthForm } from './EmailAuthForm'
import { EmailLinkConfirm } from './EmailLinkConfirm'

const highlights = [
  {
    icon: SparklesIcon,
    title: 'Describe the call',
    text: 'Tell VOYP what you need — a booking, a quote, an appointment.',
  },
  {
    icon: PhoneCallIcon,
    title: 'VOYP makes it',
    text: 'Your AI agent dials the real phone number and talks for you.',
  },
  {
    icon: MicIcon,
    title: 'Listen in live',
    text: 'Hear the conversation, take over, or answer copilot questions.',
  },
  {
    icon: FileTextIcon,
    title: 'Get the outcome',
    text: 'Full transcript, summary and recording after every call.',
  },
]

export default function LoginPage() {
  const { signInWithGoogle, signInWithApple, emailLinkStatus } = useAuth()

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2.5">
          <img src="/icons/icon-96.png" alt="" className="size-8 rounded-lg" />
          <span className="font-display text-lg font-bold tracking-tight">VOYP</span>
        </div>
        <ThemeToggle />
      </header>

      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center justify-center gap-12 px-6 py-12 lg:flex-row lg:gap-20">
        <div className="max-w-md space-y-8 text-center lg:text-left">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground">
              <SoundWave active className="h-3 text-primary" />
              Your AI call agent
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-balance sm:text-5xl">
              The phone call, <span className="text-primary">made for you</span>
            </h1>
            <p className="text-lg text-muted-foreground text-pretty">
              Describe what you need in a sentence. VOYP picks up the phone, speaks with a human
              voice, and reports back with a transcript.
            </p>
          </div>

          <div className="mx-auto flex w-64 flex-col gap-3 lg:mx-0">
            {emailLinkStatus !== 'none' ? (
              <EmailLinkConfirm />
            ) : (
              <>
                <Button size="lg" className="w-full gap-3" onClick={() => void signInWithGoogle()}>
                  <GoogleMark />
                  Continue with Google
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full gap-3"
                  onClick={() => void signInWithApple()}
                >
                  <AppleMark />
                  Continue with Apple
                </Button>

                <div className="flex items-center gap-3 py-1 text-xs text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  or
                  <span className="h-px flex-1 bg-border" />
                </div>

                <EmailAuthForm />
              </>
            )}
          </div>
        </div>

        <div className="grid w-full max-w-sm gap-3">
          {highlights.map(({ icon: Icon, title, text }, i) => (
            <div
              key={title}
              className="flex items-start gap-4 rounded-xl border bg-card p-4 shadow-xs"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="size-4.5" />
              </div>
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{title}</p>
                <p className="text-sm text-muted-foreground">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </main>

      <footer className="px-6 py-4 text-center text-xs text-muted-foreground">
        <a href="https://voyp.app/terms.html" className="hover:text-foreground" target="_blank" rel="noreferrer">
          Terms
        </a>
        <span className="mx-2">·</span>
        <a href="https://voyp.app/privacy.html" className="hover:text-foreground" target="_blank" rel="noreferrer">
          Privacy
        </a>
      </footer>
    </div>
  )
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.17 3.57-8.81Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.93-2.91l-3.87-3c-1.07.72-2.44 1.14-4.06 1.14-3.12 0-5.77-2.11-6.71-4.95H1.29v3.1A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.29 14.28a7.2 7.2 0 0 1 0-4.56v-3.1H1.29a12 12 0 0 0 0 10.76l4-3.1Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.77c1.76 0 3.34.6 4.58 1.79l3.44-3.44A11.98 11.98 0 0 0 1.29 6.62l4 3.1C6.23 6.88 8.88 4.77 12 4.77Z"
      />
    </svg>
  )
}

function AppleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-4 fill-current" aria-hidden>
      <path d="M16.36 12.94c.03 3.05 2.68 4.07 2.71 4.08-.02.07-.42 1.45-1.4 2.87-.84 1.23-1.72 2.45-3.1 2.48-1.36.02-1.79-.8-3.34-.8-1.55 0-2.03.77-3.31.83-1.33.05-2.35-1.33-3.2-2.55C2.98 17.35 1.63 12.76 3.42 9.7a4.97 4.97 0 0 1 4.19-2.54c1.31-.03 2.54.88 3.34.88.8 0 2.3-1.09 3.87-.93.66.03 2.51.27 3.7 2-.1.06-2.21 1.29-2.16 3.83ZM13.79 5.45c.71-.86 1.19-2.05 1.06-3.24-1.02.04-2.26.68-2.99 1.54-.66.76-1.23 1.97-1.08 3.13 1.14.09 2.3-.58 3-1.43Z" />
    </svg>
  )
}
